import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
} from "@mui/material";
import {
  CustomDialogProps,
  SmartAutoComplete,
  SmartForm,
  SmartSubmit,
} from "@nodeon/react-components";
import { useSnackbar } from "notistack";
import React from "react";
import { useTranslation } from "react-i18next";
import {
  ApiResponse,
  useOrganizationApi,
  useUserApi,
} from "~/api/GeneratedApi";
import { useCustomQuery } from "~/utils/hooks/useCustomQuery";

interface FormProps {
  userId: number | null;
  roleIds: number[];
}

interface Props {
  organizationId: number;
  onSubmitted?: () => void;
}

const OrganizationUserMoveDialog = ({
  handleClose,
  onSubmitted,
  organizationId,
}: CustomDialogProps & Props): JSX.Element => {
  const organizationApi = useOrganizationApi();
  const userApi = useUserApi();
  const { t } = useTranslation("organization-user-move");
  const { enqueueSnackbar } = useSnackbar();

  const { isLoading: isLoadingRoles, data: roles } = useCustomQuery(
    { key: "organization-roles", id: organizationId },
    () => organizationApi.getOrganizationRoles(organizationId),
  );

  const { isLoading: isLoadingUsers, data: users } = useCustomQuery(
    "users",
    () => userApi.getUsers(),
  );

  const onSuccess = () => {
    enqueueSnackbar(t("added"), {
      variant: "success",
    });
    onSubmitted?.();
    handleClose?.();
  };

  const onSubmit = (data: FormProps) => {
    return organizationApi.changeUserOrganization(organizationId, {
      userId: data.userId!,
      roleIds: data.roleIds,
    });
  };

  return (
    <Dialog open onClose={handleClose} aria-labelledby="form-dialog-title">
      <SmartForm<FormProps, ApiResponse>
        onSuccess={onSuccess}
        onSubmit={onSubmit}
        noGrid
        formProps={{ defaultValues: { userId: null, roleIds: [] } }}
      >
        <DialogTitle id="form-dialog-title">{t("title")}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <SmartAutoComplete<FormProps, number>
                options={
                  users?.results
                    .filter((x) => x.organizationId !== organizationId)
                    .map((x) => x.id) ?? []
                }
                fullWidth
                disabled={isLoadingUsers}
                name="userId"
                label={t("user")}
                getOptionLabel={(opt) =>
                  users?.results.find((x) => x.id === opt)?.name ??
                  t("general:unknown")
                }
                controllerProps={{
                  rules: {
                    required: { value: true, message: t("form:required") },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <SmartAutoComplete<FormProps, number, true>
                options={roles?.results.map((x) => x.id) ?? []}
                fullWidth
                multiple
                disabled={isLoadingRoles}
                name="roleIds"
                label={t("role")}
                getOptionLabel={(opt) =>
                  roles?.results.find((x) => x.id === opt)?.roleName ??
                  t("general:unknown")
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            {t("general:cancel")}
          </Button>
          <SmartSubmit>{t("move")}</SmartSubmit>
        </DialogActions>
      </SmartForm>
    </Dialog>
  );
};

export default OrganizationUserMoveDialog;
